// Feature placement: which props stand on each hex of a chunk, and where. Pure (no GL); it runs in
// the mesh worker right after the chunk mesh is built, so every prop stands on the ground that
// chunk draws (NEAR and MID meshes differ in height).
//
//   hex ─► kind (woods, rainforest, mountain, marsh, open plains / tundra / desert)
//       ─► candidate points (jittered rings) ─► keep by density, skip rivers and shores
//       ─► record: slot, position, yaw, scale, extra
//
// Records are grouped into BLOCK × BLOCK hex blocks with bounding boxes: the main thread culls
// blocks against the frustum and merges the visible ones into one InstancedMesh per slot.
//
// Only data-backed features get trees and peaks (woods, rainforest, marsh, mountains). Shrubs and
// stones on open ground are texture-scale decoration and never cluster into anything that reads
// as a marker (ARCHITECTURE §1 rule 10).

import { COLS, WORLD_W, neighbour } from '../../core/grid.ts';
import { FEATURE, HILL, TERRAIN } from '../../core/schema.ts';
import type { PhysicalGrid } from '../hexdata.ts';
import { HEX_KIND, NO_SNOW, WOODS_KIND, capFraction, hexHash, peakHero, peakShare, rollingGround, woodsKind, type HexShapes } from '../terrain/heights.ts';

/** One instanced model. `file` is relative to the asset base URL, or `peak:…` for a model built in code. */
export interface SlotDef {
  file: string;
  kind: 'tree' | 'peak' | 'plant' | 'building';
  /** Model height at scale 1 (docs/ASSETS.md, public/assets/manifest.json). */
  height: number;
  /** Peaks: a horn on the highest hex of a massif, or a low lump (features/peakGeo.ts). */
  role?: 'hero' | 'shoulder';
}

/** Prefix of the slots built in code (features/peakGeo.ts peakSpec). */
export const PEAK_FILE_PREFIX = 'peak:';

const M = 'models/';

export const SLOTS: readonly SlotDef[] = [
  { file: `${M}tree.conifer/tree_pineTallA_detailed.glb`, kind: 'tree', height: 0.298 },
  { file: `${M}tree.conifer/tree_pineTallB_detailed.glb`, kind: 'tree', height: 0.33 },
  { file: `${M}tree.conifer/tree_cone.glb`, kind: 'tree', height: 0.282 },
  { file: `${M}tree.broadleaf/tree_default.glb`, kind: 'tree', height: 0.3 },
  { file: `${M}tree.broadleaf/tree_simple.glb`, kind: 'tree', height: 0.286 },
  { file: `${M}tree.broadleaf/tree_fat.glb`, kind: 'tree', height: 0.244 },
  { file: `${M}tree.jungle/tree_plateau.glb`, kind: 'tree', height: 0.28 },
  { file: `${M}tree.jungle/tree_default.glb`, kind: 'tree', height: 0.36 },
  { file: `${M}tree.broadleaf/tree_small.glb`, kind: 'tree', height: 0.23 },
  { file: `${M}tree.palm/tree_palmTall.glb`, kind: 'tree', height: 0.26 },
  { file: 'peak:hero-alpine-0', kind: 'peak', height: 1, role: 'hero' },
  { file: 'peak:hero-alpine-1', kind: 'peak', height: 1, role: 'hero' },
  { file: 'peak:hero-alpine-2', kind: 'peak', height: 1, role: 'hero' },
  { file: 'peak:hero-arid-0', kind: 'peak', height: 1, role: 'hero' },
  { file: 'peak:hero-arid-1', kind: 'peak', height: 1, role: 'hero' },
  { file: `${M}marsh.reeds/reeds-2.glb`, kind: 'plant', height: 0.1 },
  { file: `${M}marsh.reeds/reeds-3.glb`, kind: 'plant', height: 0.13 },
  { file: `${M}shrub.green/plant_bushLarge.glb`, kind: 'plant', height: 0.08 },
  { file: `${M}shrub.green/plant_bushLargeTriangle.glb`, kind: 'plant', height: 0.06 },
  // Cities (features/cities.ts): old-town houses with owner-coloured roofs, low-detail blocks,
  // towers for the largest cities, a generic capitol for capitals.
  { file: `${M}building.oldtown/building_home_A.glb`, kind: 'building', height: 0.1 },
  { file: `${M}building.oldtown/building_home_B.glb`, kind: 'building', height: 0.14 },
  { file: `${M}building.lod/low-detail-building-a.glb`, kind: 'building', height: 0.35 },
  { file: `${M}building.lod/low-detail-building-d.glb`, kind: 'building', height: 0.25 },
  { file: `${M}building.lod/low-detail-building-e.glb`, kind: 'building', height: 0.3 },
  { file: `${M}building.lod/low-detail-building-wide-a.glb`, kind: 'building', height: 0.2 },
  { file: `${M}building.tower/building-skyscraper-b.glb`, kind: 'building', height: 0.625 },
  { file: `${M}building.tower/building-skyscraper-d.glb`, kind: 'building', height: 0.7 },
  { file: `${M}landmark.capitol/capitol-1.glb`, kind: 'building', height: 0.26 },
  { file: 'peak:far-alpine', kind: 'peak', height: 1, role: 'hero' },
  { file: 'peak:shoulder-alpine-0', kind: 'peak', height: 1, role: 'shoulder' },
  { file: 'peak:shoulder-arid-0', kind: 'peak', height: 1, role: 'shoulder' },
  // More city buildings, so cities differ by climate (features/cities.ts STYLES).
  { file: `${M}building.midrise/building_A_withoutBase.glb`, kind: 'building', height: 0.16 },
  { file: `${M}building.midrise/building_D_withoutBase.glb`, kind: 'building', height: 0.2 },
  { file: `${M}building.midrise/building_F_withoutBase.glb`, kind: 'building', height: 0.23 },
  { file: `${M}building.office/building-a.glb`, kind: 'building', height: 0.2 },
  { file: `${M}building.office/building-d.glb`, kind: 'building', height: 0.24 },
  { file: `${M}building.house/building-type-a.glb`, kind: 'building', height: 0.1 },
  { file: `${M}building.house/building-type-g.glb`, kind: 'building', height: 0.097 },
  { file: `${M}building.house/building-type-k.glb`, kind: 'building', height: 0.12 },
  { file: `${M}building.tower/building-skyscraper-a.glb`, kind: 'building', height: 0.4 },
  { file: `${M}building.tower/building-skyscraper-c.glb`, kind: 'building', height: 0.475 },
  { file: `${M}building.tower/building-skyscraper-e.glb`, kind: 'building', height: 0.55 },
  // More lumps, so the foothills of a range do not repeat one shape.
  { file: 'peak:shoulder-alpine-1', kind: 'peak', height: 1, role: 'shoulder' },
  { file: 'peak:shoulder-alpine-2', kind: 'peak', height: 1, role: 'shoulder' },
  { file: 'peak:shoulder-arid-1', kind: 'peak', height: 1, role: 'shoulder' },
];

/** Building slot indices (into SLOTS) for features/cities.ts. */
export const BUILDING = {
  OLDTOWN: [19, 20],
  BLOCK: [21, 22, 23, 24],
  TOWER: [25, 26],
  CAPITOL: 27,
  MIDRISE: [31, 32, 33],
  OFFICE: [34, 35],
  HOUSE: [36, 37, 38],
  SKYSCRAPER: [39, 40, 41],
} as const;

// Slot groups (indices into SLOTS).
const CONIFER = [0, 1, 2] as const;
const BROADLEAF = [3, 4, 5] as const;
const JUNGLE = [6, 7, 8] as const;
const PALM = 9;
const PEAK_ALPINE = [10, 11, 12] as const;
const PEAK_ARID = [13, 14] as const;
const FAR_PEAK = 28; // MID chunks: the low-poly horn
const SHOULDER_ALPINE = [29, 42, 43] as const;
const SHOULDER_ARID = [30, 44] as const;
const REEDS = [15, 16] as const;
const SHRUB_GREEN = [17, 18] as const;

/** Floats per record: slot, x, y, z, yaw, scale xz, scale y, extra (peaks: snow line y). */
export const REC = 8;
/** Floats per block: start record, count, min x, y, z, max x, y, z. */
export const BLOCK_REC = 8;
/** Block size in hexes. */
export const BLOCK = 8;

export interface FeatureData {
  rec: Float32Array;
  blocks: Float32Array;
}

/** Ground height inside hex `t` at world (x, z), from the chunk mesh just built. */
export type GroundFn = (t: number, x: number, z: number) => number;

/** A disc of ground (map units) that props keep off: the decal of a lake smaller than a hex. */
export interface Clearing {
  x: number;
  z: number;
  r: number;
}

export interface PlaceInput {
  grid: PhysicalGrid;
  shapes: HexShapes;
  /** 'near' = full props, 'mid' = clumps and peaks only. */
  detail: 'near' | 'mid';
  ground: GroundFn;
  /** Owned hexes of the chunk and their centres. */
  hexes: ReadonlyArray<{ t: number; cx: number; cz: number }>;
  /** First column and row of the chunk. */
  c0: number;
  r0: number;
  clearings?: readonly Clearing[];
}

// Candidate points: a jittered hex lattice inside the hex (centre + ring of 6 + ring of 12).
const LATTICE_STEP = 0.29;
const LATTICE_JITTER = 0.1;
const LATTICE: ReadonlyArray<readonly [number, number]> = (() => {
  const pts: Array<[number, number]> = [[0, 0]];
  for (let k = 0; k < 6; k++) {
    const a = (Math.PI / 3) * k;
    pts.push([Math.cos(a) * LATTICE_STEP, Math.sin(a) * LATTICE_STEP]);
  }
  for (let k = 0; k < 12; k++) {
    const a = (Math.PI / 6) * k;
    const r = LATTICE_STEP * (k % 2 === 0 ? 2 : Math.sqrt(3));
    pts.push([Math.cos(a) * r, Math.sin(a) * r]);
  }
  return pts;
})();

// Densities (share of lattice points kept) and scales.
const WOODS_KEEP = 0.56;
const RAINFOREST_KEEP = 0.72;
const TREE_SCALE = [1.3, 1.75] as const;
const JUNGLE_SCALE = [1.45, 1.95] as const;
const TREE_STRETCH = [0.88, 1.18] as const; // height scale relative to width scale
const MID_TREE_SCALE = 2.1;
const MID_TREE_RING = 0.42;
const MID_WOODS_TREES = 3;
const MID_JUNGLE_TREES = 4;
const PALM_SHARE = 0.12;

// Keep-outs, as distance from a hex edge (world units).
const RIVER_CLEAR = 0.24;
const SHORE_CLEAR = 0.3;
const EDGE_CLEAR = 0.05;

// Peaks: a hero horn on the highest hex of a massif (heights.ts peakHero), sized by the hex's
// highest point, and up to two shoulders that lean toward mountain neighbours. Every other mountain
// hex holds low lumps: one on its centre and up to two toward mountain neighbours, so a range reads
// as ridge lines around a few massifs, not a carpet of same-size cones.
// Every model stays inside its own hex: political borders run along hex edges and must stay visible.
const HERO_RADIUS = [0.42, 0.62] as const; // footprint radius by scale share
const LUMP_RADIUS = [0.34, 0.5] as const; // the centre lump of a hex without a horn
const LUMP_SIDE_AT = 0.4; // scale share where a lump-only hex gets a second ridge lump
const LUMP_SIDE_HEIGHT = [0.6, 0.85] as const; // ridge lumps, as a share of the centre lump's height
const HERO_RADIUS_JITTER = 0.15; // ± share of the radius, so ranges are not a lattice of equal cones
const HERO_SHIFT = 0.3; // largest offset from the hex centre, while the model stays in its hex
const HERO_REACH = 1.4; // a model's ridge spurs reach this many footprint radii
const HEX_REACH = 1.0; // hex circumradius
const HERO_FOOT_RING = 0.8; // ground sampled on this share of the radius under the base
const HERO_SINK = 0.04;
const SHOULDER_AT = [0.25, 0.6] as const; // scale share where the 1st and 2nd shoulder appear
const SHOULDER_REACH = 0.5; // from the hex centre toward a neighbour's edge
const SHOULDER_RADIUS = [0.25, 0.32] as const;
const SHOULDER_HEIGHT = [0.3, 0.5] as const; // share of the hero's height
const SHOULDER_SPREAD = 0.1; // sideways jitter along the edge
const PEAK_LAKE_GAP = 0.3; // a horn stands this far off a small lake's rim
const PEAK_LAKE_SHIFT = 0.35; // and moves at most this far to get there
const PROP_LAKE_GAP = 0.06;

// Camera heights (hex radii) above which peaks thin out: shoulders first, then the low heroes.
const THIN_HEIGHTS = [30, 60] as const;
const LOW_HERO_HEIGHT = 1.05; // heroes below this (world units) drop at the farthest level

// Ground props on open land.
const MARSH_REEDS = 6;
const SHRUB_SCALE = [2.2, 3.2] as const;

// Plains: a few tufts, each a huddle of small bushes, not single big ones spaced like dots.
const PLAINS_TUFTS = 2;
const TUFT_SIZE = 3;
const TUFT_RADIUS = 0.1;
const TUFT_SCALE = [1.2, 2] as const;

const SQRT3 = Math.sqrt(3);
const INRADIUS = SQRT3 / 2;
const EDGE_N: ReadonlyArray<readonly [number, number]> = Array.from({ length: 6 }, (_, d) => [Math.cos((Math.PI / 3) * d), Math.sin((Math.PI / 3) * d)] as const);

// Model radius used to pad block bounds.
const PAD = { tree: 0.35, peak: 1.45, plant: 0.2, building: 0.4 } as const;

class Rng {
  private n = 0;
  private readonly t: number;
  constructor(t: number, salt: number) {
    this.t = t * 7 + salt;
  }
  next(): number {
    return hexHash(this.t, 100 + this.n++);
  }
  range(r: readonly [number, number]): number {
    return r[0] + (r[1] - r[0]) * this.next();
  }
  pick<T>(list: readonly T[]): T {
    return list[Math.min(list.length - 1, Math.floor(this.next() * list.length))];
  }
}

function isWaterTerrain(t: number): boolean {
  return t === TERRAIN.OCEAN || t === TERRAIN.COAST || t === TERRAIN.LAKE;
}

/** Edge bits (bit d) where the neighbour is water, and the river bits. */
export function keepOuts(g: PhysicalGrid, t: number): { shore: number; river: number } {
  let shore = 0;
  for (let d = 0; d < 6; d++) {
    const n = neighbour(t, d);
    if (n >= 0 && isWaterTerrain(g.terrain[n])) {
      shore |= 1 << d;
    }
  }
  return { shore, river: g.river[t] };
}

/** True when local point (lx, lz) keeps clear of rivers, shores and the hex rim. */
export function clear(lx: number, lz: number, k: { shore: number; river: number }): boolean {
  for (let d = 0; d < 6; d++) {
    const dist = INRADIUS - (lx * EDGE_N[d][0] + lz * EDGE_N[d][1]);
    if (dist < EDGE_CLEAR) {
      return false;
    }
    if ((k.river & (1 << d)) !== 0 && dist < RIVER_CLEAR) {
      return false;
    }
    if ((k.shore & (1 << d)) !== 0 && dist < SHORE_CLEAR) {
      return false;
    }
  }
  return true;
}

/** The first clearing that holds (x, z) once its radius grows by `gap`: offset, distance, radius. */
function clearingAt(input: PlaceInput, x: number, z: number, gap: number): { dx: number; dz: number; dist: number; r: number } | null {
  for (const c of input.clearings ?? []) {
    const dxRaw = x - c.x;
    const dx = dxRaw - Math.round(dxRaw / WORLD_W) * WORLD_W;
    const dz = z - c.z;
    const dist = Math.hypot(dx, dz);
    if (dist < c.r + gap) {
      return { dx, dz, dist, r: c.r };
    }
  }
  return null;
}

interface Sink {
  out: number[];
  push(slot: number, x: number, y: number, z: number, yaw: number, sxz: number, sy: number, extra: number): void;
}

/** Place props for every owned hex of a chunk. */
export function placeChunk(input: PlaceInput): FeatureData {
  const perBlock = new Map<number, number[]>();
  for (const h of input.hexes) {
    const block = blockOf(h.t, input.c0, input.r0);
    let out = perBlock.get(block);
    if (!out) {
      out = [];
      perBlock.set(block, out);
    }
    const sink: Sink = {
      out,
      push(slot, x, y, z, yaw, sxz, sy, extra) {
        out.push(slot, x, y, z, yaw, sxz, sy, extra);
      },
    };
    placeHex(input, h.t, h.cx, h.cz, sink);
  }
  return packBlocks(perBlock);
}

/** Block index of hex t inside the chunk whose first column and row are c0, r0. */
export function blockOf(t: number, c0: number, r0: number): number {
  const col = t % COLS;
  const row = Math.floor(t / COLS);
  return Math.floor((row - r0) / BLOCK) * 16 + Math.floor((col - c0) / BLOCK);
}

function placeHex(input: PlaceInput, t: number, cx: number, cz: number, sink: Sink): void {
  const { grid: g, shapes } = input;
  if (shapes.kind[t] !== HEX_KIND.LAND) {
    return;
  }
  const f = g.feature[t];
  const rng = new Rng(t, 0);

  if (g.hill[t] === HILL.MOUNTAINS && !rollingGround(g, t)) {
    placePeak(input, t, cx, cz, sink, rng);
  }
  if (f === FEATURE.WOODS || f === FEATURE.RAINFOREST) {
    placeTrees(input, t, cx, cz, sink, rng);
    return;
  }
  if (input.detail === 'mid' || g.hill[t] === HILL.MOUNTAINS || f === FEATURE.ICE) {
    return;
  }
  if (f === FEATURE.MARSH) {
    placeScatter(input, t, cx, cz, sink, rng, { slots: REEDS, count: MARSH_REEDS, scale: SHRUB_SCALE, kind: 'plant' });
    return;
  }
  if (f !== FEATURE.NONE) {
    return;
  }
  // Open plains get a few low bushes; tundra and desert stay bare (tiny props there read as specks).
  if (g.terrain[t] === TERRAIN.PLAINS) {
    placeScatter(input, t, cx, cz, sink, rng, { slots: SHRUB_GREEN, count: PLAINS_TUFTS, scale: TUFT_SCALE, kind: 'plant', huddle: { size: TUFT_SIZE, radius: TUFT_RADIUS } });
  }
}

function placeTrees(input: PlaceInput, t: number, cx: number, cz: number, sink: Sink, rng: Rng): void {
  const { grid: g } = input;
  const jungle = g.feature[t] === FEATURE.RAINFOREST;
  const kind = jungle ? WOODS_KIND.BROADLEAF : woodsKind(g, t);
  const pickSlot = (): number => {
    if (jungle) {
      return rng.next() < PALM_SHARE ? PALM : rng.pick(JUNGLE);
    }
    if (kind === WOODS_KIND.CONIFER) {
      return rng.pick(CONIFER);
    }
    if (kind === WOODS_KIND.MIXED) {
      return rng.next() < 0.5 ? rng.pick(CONIFER) : rng.pick(BROADLEAF);
    }
    return rng.pick(BROADLEAF);
  };
  const scaleRange = jungle ? JUNGLE_SCALE : TREE_SCALE;
  const k = keepOuts(g, t);
  const mountain = g.hill[t] === HILL.MOUNTAINS;

  // MID: a few big trees on a ring, the same count everywhere (a clump per hex).
  if (input.detail === 'mid') {
    const n = jungle ? MID_JUNGLE_TREES : MID_WOODS_TREES;
    const spin = rng.next() * Math.PI * 2;
    for (let i = 0; i < n; i++) {
      const a = spin + (i / n) * Math.PI * 2;
      const lx = Math.cos(a) * MID_TREE_RING;
      const lz = Math.sin(a) * MID_TREE_RING;
      const slot = pickSlot();
      const s = MID_TREE_SCALE * rng.range([0.85, 1.1]);
      emit(input, sink, t, slot, cx + lx, cz + lz, rng.next() * Math.PI * 2, s, s * rng.range(TREE_STRETCH), 0);
    }
    return;
  }

  // NEAR: a jittered lattice, thinned; mountains keep trees on their lower rim only.
  const spin = rng.next() * Math.PI * 2;
  const cs = Math.cos(spin);
  const sn = Math.sin(spin);
  const keep = jungle ? RAINFOREST_KEEP : WOODS_KEEP;
  for (const [px, pz] of LATTICE) {
    if (rng.next() > keep) {
      continue;
    }
    const lx = px * cs - pz * sn + (rng.next() - 0.5) * 2 * LATTICE_JITTER;
    const lz = px * sn + pz * cs + (rng.next() - 0.5) * 2 * LATTICE_JITTER;
    if (mountain && Math.hypot(lx, lz) < 0.6) {
      continue;
    }
    if (!clear(lx, lz, k)) {
      continue;
    }
    const slot = pickSlot();
    const s = rng.range(scaleRange);
    emit(input, sink, t, slot, cx + lx, cz + lz, rng.next() * Math.PI * 2, s, s * rng.range(TREE_STRETCH), 0);
  }
}

/** Where a mountain hex's main peak model stands (before any small-lake shift) and its footprint radius. */
export function peakFootprint(g: PhysicalGrid, t: number, cx: number, cz: number): { x: number; z: number; r: number } {
  const size = peakHero(g, t) ? HERO_RADIUS : LUMP_RADIUS;
  const share = size[0] + (size[1] - size[0]) * peakShare(g, t);
  const r = share * (1 + (hexHash(t, 21) - 0.5) * 2 * HERO_RADIUS_JITTER);

  // The offset uses the room the model leaves inside the hex (per axis: the diagonal stays within it).
  const room = Math.max(0, Math.min(HERO_SHIFT, HEX_REACH - r * HERO_REACH)) / Math.SQRT2;
  return { x: cx + (hexHash(t, 1) - 0.5) * 2 * room, z: cz + (hexHash(t, 2) - 0.5) * 2 * room, r };
}

/** Thinning level for a camera height: 0 keeps every peak, 1 drops shoulders, 2 also low heroes. */
export function peakThin(camHeight: number): number {
  return camHeight > THIN_HEIGHTS[1] ? 2 : camHeight > THIN_HEIGHTS[0] ? 1 : 0;
}

/** Whether a peak of `role` and height `sy` stays at thinning level `level`. */
export function peakKept(level: number, role: SlotDef['role'], sy: number): boolean {
  if (role === 'shoulder') {
    return level === 0;
  }
  return level < 2 || sy >= LOW_HERO_HEIGHT;
}

function placePeak(input: PlaceInput, t: number, cx: number, cz: number, sink: Sink, rng: Rng): void {
  const { grid: g, shapes } = input;
  const horn = peakHero(g, t);

  // Far chunks draw the massifs only: a hex without a horn shows its ground.
  if (input.detail === 'mid' && !horn) {
    return;
  }
  const arid = g.terrain[t] === TERRAIN.DESERT || (g.terrain[t] === TERRAIN.PLAINS && g.ice[t] === 0);
  const main = peakFootprint(g, t, cx, cz);
  let { x, z } = main;

  // A small lake in the hex: slide the horn off it, so the lake decal shows.
  const lake = clearingAt(input, x, z, PEAK_LAKE_GAP);
  if (lake) {
    const away = lake.dist > 0 ? { x: lake.dx / lake.dist, z: lake.dz / lake.dist } : { x: 1, z: 0 };
    const move = Math.min(PEAK_LAKE_SHIFT, lake.r + PEAK_LAKE_GAP - lake.dist);
    x += away.x * move;
    z += away.z * move;
  }

  const lumps = arid ? SHOULDER_ARID : SHOULDER_ALPINE;
  const height = shapes.peakH[t];
  const slot = input.detail === 'mid' ? FAR_PEAK : horn ? rng.pick(arid ? PEAK_ARID : PEAK_ALPINE) : rng.pick(lumps);
  const y = footing(input, t, x, z, main.r);
  const cap = capFraction(g, t);
  const snowLine = cap < 1 ? y + height * cap : NO_SNOW;
  sink.push(slot, x, y, z, rng.next() * Math.PI * 2, main.r, height, snowLine);
  if (input.detail === 'mid') {
    return;
  }

  // Shoulders (beside a horn) or ridge lumps (beside a lump) on the ridge toward mountain
  // neighbours, in a stable order.
  const share = peakShare(g, t);
  const shoulders = share >= SHOULDER_AT[1] ? 2 : share >= SHOULDER_AT[0] ? 1 : 0;
  const count = horn ? shoulders : share >= LUMP_SIDE_AT ? 2 : 1;
  const sizes = horn ? SHOULDER_HEIGHT : LUMP_SIDE_HEIGHT;
  const toward: number[] = [];
  for (let d = 0; d < 6; d++) {
    const n = neighbour(t, d);
    if (n >= 0 && g.hill[n] === HILL.MOUNTAINS && !rollingGround(g, n) && shapes.kind[n] === HEX_KIND.LAND) {
      toward.push(d);
    }
  }
  const first = Math.floor(hexHash(t, 3) * toward.length);
  for (let i = 0; i < count && i < toward.length; i++) {
    const a = (Math.PI / 3) * toward[(first + i) % toward.length];
    const side = (hexHash(t, 4 + i) - 0.5) * 2 * SHOULDER_SPREAD;
    const sx = cx + Math.cos(a) * SHOULDER_REACH - Math.sin(a) * side;
    const sz = cz + Math.sin(a) * SHOULDER_REACH + Math.cos(a) * side;
    const r = SHOULDER_RADIUS[0] + (SHOULDER_RADIUS[1] - SHOULDER_RADIUS[0]) * hexHash(t, 6 + i);
    if (clearingAt(input, sx, sz, PROP_LAKE_GAP + r)) {
      continue;
    }
    const h = height * (sizes[0] + (sizes[1] - sizes[0]) * hexHash(t, 8 + i));
    sink.push(rng.pick(lumps), sx, footing(input, t, sx, sz, r), sz, rng.next() * Math.PI * 2, r, h, snowLine);
  }
}

/** The base of a model of footprint radius r sits on the lowest ground under its rim, so no foot floats. */
function footing(input: PlaceInput, t: number, x: number, z: number, r: number): number {
  let y = Infinity;
  for (let k = 0; k < 6; k++) {
    const a = (Math.PI / 3) * k + Math.PI / 6;
    y = Math.min(y, input.ground(t, x + Math.cos(a) * r * HERO_FOOT_RING, z + Math.sin(a) * r * HERO_FOOT_RING));
  }
  return Math.min(y, input.ground(t, x, z)) - HERO_SINK;
}

interface Scatter {
  slots: readonly number[];
  count: number;
  scale: readonly [number, number];
  kind: SlotDef['kind'];
  /** Each of `count` spots becomes `size` props within `radius` of it. */
  huddle?: { size: number; radius: number };
}

function placeScatter(input: PlaceInput, t: number, cx: number, cz: number, sink: Sink, rng: Rng, s: Scatter): void {
  const k = keepOuts(input.grid, t);
  for (let i = 0; i < s.count; i++) {
    const a = rng.next() * Math.PI * 2;
    const r = Math.sqrt(rng.next()) * 0.72;
    const lx = Math.cos(a) * r;
    const lz = Math.sin(a) * r;
    if (!clear(lx, lz, k)) {
      continue;
    }
    if (!s.huddle) {
      const scale = rng.range(s.scale);
      emit(input, sink, t, rng.pick(s.slots), cx + lx, cz + lz, rng.next() * Math.PI * 2, scale, scale * rng.range([0.8, 1.2]), 0);
      continue;
    }

    for (let m = 0; m < s.huddle.size; m++) {
      const ma = rng.next() * Math.PI * 2;
      const mr = Math.sqrt(rng.next()) * s.huddle.radius;
      const mx = lx + Math.cos(ma) * mr;
      const mz = lz + Math.sin(ma) * mr;
      const scale = rng.range(s.scale);
      const slot = rng.pick(s.slots);
      const yaw = rng.next() * Math.PI * 2;
      if (clear(mx, mz, k)) {
        emit(input, sink, t, slot, cx + mx, cz + mz, yaw, scale, scale * rng.range([0.8, 1.2]), 0);
      }
    }
  }
}

/** Record a prop standing on the ground at (x, z). */
function emit(input: PlaceInput, sink: Sink, t: number, slot: number, x: number, z: number, yaw: number, sxz: number, sy: number, extra: number): void {
  if (clearingAt(input, x, z, PROP_LAKE_GAP)) {
    return;
  }
  const y = input.ground(t, x, z) - 0.01;
  sink.push(slot, x, y, z, yaw, sxz, sy, extra);
}

/** Concatenate block record lists and compute each block's bounds. */
export function packBlocks(perBlock: Map<number, number[]>): FeatureData {
  const keys = [...perBlock.keys()].sort((a, b) => a - b);
  let total = 0;
  for (const k of keys) {
    total += (perBlock.get(k) as number[]).length;
  }
  const rec = new Float32Array(total);
  const blocks: number[] = [];
  let at = 0;
  for (const k of keys) {
    const list = perBlock.get(k) as number[];
    if (list.length === 0) {
      continue;
    }
    rec.set(list, at);
    const box = [Infinity, Infinity, Infinity, -Infinity, -Infinity, -Infinity];
    for (let i = 0; i < list.length; i += REC) {
      const slot = SLOTS[list[i]];
      const pad = PAD[slot.kind] * list[i + 5];
      const top = slot.height * list[i + 6];
      box[0] = Math.min(box[0], list[i + 1] - pad);
      box[1] = Math.min(box[1], list[i + 2]);
      box[2] = Math.min(box[2], list[i + 3] - pad);
      box[3] = Math.max(box[3], list[i + 1] + pad);
      box[4] = Math.max(box[4], list[i + 2] + top);
      box[5] = Math.max(box[5], list[i + 3] + pad);
    }
    blocks.push(at / REC, list.length / REC, ...box);
    at += list.length;
  }
  return { rec, blocks: new Float32Array(blocks) };
}
